import { useMemo } from 'react';
import { type Resolve } from '@doxyme/types';
import { type SessionParticipant } from '../../webrtc/session';
import { getSessionParticipantsMap } from '../../features/session/session.selectors';
import { getAllMembersMap } from '../../features/realtime/realtime.selectors';
import { getChatUserIdFromIdentity } from '../../features/realtime/realtime.helpers';
import {
  type RealtimeMember,
  RealtimeRole,
} from '../../features/realtime/realtime.types';
import { useAppSelector } from '../useReduxToolkit';

export type PatientViewRealtimeCallParticipant = Resolve<
  Partial<RealtimeMember> & SessionParticipant & { chatUserId?: string }
>;

export type PatientViewSipCallParticipant = Resolve<
  /**
   * Added here to avoid changes in a lot of places especially in
   */
  SessionParticipant & {
    isTestPatient: false;
    picture?: string;
    identity: { id: string; role: RealtimeRole.Patient };
  }
>;

export type PatientViewCallParticipant =
  | PatientViewRealtimeCallParticipant
  | PatientViewSipCallParticipant;

export type UsePatientViewCallParticipantsReturnType = {
  /**
   * Map of current call participants, keyed by participant ID.
   */
  callParticipantsMap: Record<string, PatientViewCallParticipant>;
  /**
   * Map of realtime call participants, keyed by participant ID.
   */
  realtimeCallParticipantsMap: Record<
    string,
    PatientViewRealtimeCallParticipant
  >;
  /**
   * Map of sip call participants, keyed by participant ID.
   */
  sipCallParticipantsMap: Record<string, PatientViewSipCallParticipant>;
  /**
   * A list of active participants in the current call.
   * Excludes participants who have already left the call.
   */
  callParticipants: PatientViewCallParticipant[];
  /**
   * The participant currently sharing their screen, if any.
   */
  screenSharingParticipant?: PatientViewCallParticipant;
  /**
   * A list of realtime call participants.
   */
  realtimeCallParticipants: PatientViewRealtimeCallParticipant[];
  /**
   * A list of sip call participants. (Does not exists in realtime)
   */
  sipCallParticipants: PatientViewSipCallParticipant[];
};

export const usePatientViewCallParticipants =
  (): UsePatientViewCallParticipantsReturnType => {
    const sessionParticipantsMap: Record<string, SessionParticipant> =
      useAppSelector(getSessionParticipantsMap);
    const realtimeMembersMap = useAppSelector(getAllMembersMap);

    const callParticipantsMap = useMemo(
      () =>
        Object.fromEntries(
          Object.entries(sessionParticipantsMap).map(([id, participant]) => {
            const member = realtimeMembersMap[id];

            if (member && member.identity.role === RealtimeRole.Provider) {
              return [
                id,
                {
                  ...member,
                  ...participant,
                  username: member.state?.username ?? participant.username,
                  chatUserId: getChatUserIdFromIdentity(member?.identity),
                },
              ];
            }

            return [id, { ...participant, username: participant.username }];
          }),
        ),
      [sessionParticipantsMap, realtimeMembersMap],
    );

    const callParticipants = useMemo(
      () => Object.values(callParticipantsMap),
      [callParticipantsMap],
    );

    const screenSharingParticipant = useMemo(
      () => callParticipants.find((participant) => participant.screenElement),
      [callParticipants],
    );

    const realtimeCallParticipants = useMemo(
      () =>
        callParticipants.filter(
          (
            participant: PatientViewCallParticipant,
          ): participant is PatientViewRealtimeCallParticipant =>
            !participant.isSipParticipant,
        ),
      [callParticipants],
    );

    const sipCallParticipants = useMemo(
      () =>
        callParticipants.filter(
          (
            participant: PatientViewCallParticipant,
          ): participant is PatientViewSipCallParticipant =>
            !!participant.isSipParticipant,
        ),
      [callParticipants],
    );

    const realtimeCallParticipantsMap = useMemo(
      () =>
        Object.fromEntries(
          realtimeCallParticipants.map(
            (participant): [string, PatientViewRealtimeCallParticipant] => [
              participant.id,
              participant,
            ],
          ),
        ),
      [realtimeCallParticipants],
    );

    const sipCallParticipantsMap = useMemo(
      () =>
        Object.fromEntries(
          sipCallParticipants.map(
            (participant): [string, PatientViewSipCallParticipant] => [
              participant.id,
              participant,
            ],
          ),
        ),
      [sipCallParticipants],
    );

    return {
      callParticipantsMap,
      realtimeCallParticipantsMap,
      sipCallParticipantsMap,
      callParticipants,
      realtimeCallParticipants,
      sipCallParticipants,
      screenSharingParticipant,
    };
  };
